import { useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { BLUEPRINT_OPERATION_TYPES_WITH_SCHEDULES } from "../../../constants";
import { Blueprint, BlueprintTrigger } from "../../../models/Blueprints";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import useBlueprintContext from "../context/useBlueprintContext";
import { saveBlueprintAsDraft, saveBlueprintTrigger } from "../utils/BlueprintEditorAPIClient";
import { showErrorMessages } from "../utils/BlueprintEditorUtils";
import { ButtonVariant, Dialog, TextField } from "@merge-api/merge-javascript-shared";

type VersionControlSaveModalProps = {
  show: boolean;
  onHide: () => void;
  setIsPolling?: (isPolling: boolean) => void;
  setLastSavedAt?: (lastSavedAt: Date) => void;
};

export const VersionControlSaveModal = ({
  show,
  onHide,
  setIsPolling,
  setLastSavedAt,
}: VersionControlSaveModalProps) => {
  const { register, handleSubmit, setError, formState } = useForm();
  const [isLoading, setIsLoading] = useState(false);

  const history = useHistory();

  const {
    blueprint,
    blueprintTrigger,
    setOriginalBlueprint,
    setBlueprint,
    setBlueprintTrigger,
    codeBlockBranch,
  } = useBlueprintContext();
  const onSubmit = (data: any) => {
    setIsLoading(true);
    // Track time of save if it exists
    setLastSavedAt && setLastSavedAt(new Date());

    // Recursively inject code_block_branch into all code block step metadata,
    // including steps nested inside paths (e.g., inside while loops, if/else).
    const injectCodeBlockBranch = (steps: any[]): any[] =>
      steps.map((step: any) => {
        let updated = step;
        if (step.template?.step_type === "CODE_BLOCK") {
          updated = {
            ...step,
            template: {
              ...step.template,
              metadata: {
                ...step.template.metadata,
                code_block_branch: codeBlockBranch,
              },
            },
          };
        }
        if (updated.paths && typeof updated.paths === "object") {
          const updatedPaths: any = {};
          for (const [key, pathSteps] of Object.entries(updated.paths)) {
            updatedPaths[key] = Array.isArray(pathSteps)
              ? injectCodeBlockBranch(pathSteps)
              : pathSteps;
          }
          updated = { ...updated, paths: updatedPaths };
        }
        return updated;
      });

    const blueprintWithCodeBlockBranch = {
      ...blueprint,
      steps: injectCodeBlockBranch(blueprint.steps ?? []),
    };

    saveBlueprintAsDraft({
      blueprint: blueprintWithCodeBlockBranch,
      history,
      onSuccess: (blueprint: Blueprint) => {
        setBlueprint(blueprint);
        setOriginalBlueprint(blueprint);
        showSuccessToast("Saved blueprint as a draft!");
        setIsLoading(false);
        setIsPolling && setIsPolling(true);
        onHide();
      },
      onError: (err: Response | undefined) => {
        setIsLoading(false);
        showErrorMessages(err, "Failed to save blueprint as a draft.", setError);
      },
      comment: data.comment,
    });

    if (
      blueprintTrigger &&
      BLUEPRINT_OPERATION_TYPES_WITH_SCHEDULES.includes(blueprint.operation_type)
    ) {
      saveBlueprintTrigger({
        blueprintTrigger,
        blueprintID: blueprint.id,
        onSuccess: (trigger: BlueprintTrigger) => {
          setBlueprintTrigger(trigger);
        },
        onError: (_) => {
          showErrorToast("Failed to save blueprint trigger.");
        },
      });
    }
  };

  return (
    <Dialog
      title="Confirm save draft"
      open={show}
      onClose={onHide}
      variant="md"
      primaryButtonLoading={isLoading}
      primaryButtonText="Confirm save"
      primaryButtonVariant={ButtonVariant.PrimaryGray}
      onPrimaryButtonClick={() => handleSubmit(onSubmit)()}
      secondaryButtonText="Cancel"
      secondaryButtonVariant={ButtonVariant.TextBlack}
      onSecondaryButtonClick={onHide}
    >
      <TextField
        name="comment"
        type="text"
        labelTitle="Leave a comment:"
        error={!!formState.errors.comment}
        errorText="Please enter a comment for this blueprint version."
        placeholder="Enter a comment"
        ref={register({ required: true })}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSubmit(onSubmit)();
          }
        }}
      />
    </Dialog>
  );
};

export default VersionControlSaveModal;
